import React, { useState, useId } from 'react';
import type { ProjectEstimationEnrollment } from 'models/enrollments/ProjectEstimationEnrollment';
import type { TeamProjectEstimation, BillingType } from 'models/projects/TeamProjectEstimation';
import type { UserSubmissionDto } from 'models/projects/UserSubmissionDto';
import type { CurrentProjectStage } from 'models/projects/CurrentProjectStage';
import type { ProjectEstimationSubmissionDto } from 'models/projects/ProjectEstimationSubmission';

type Props = {
  enrollment: ProjectEstimationEnrollment;
  teamEstimation: TeamProjectEstimation | null;
  isOwner: boolean;
  isTeamMember: boolean;
  teamId: string;
  nodeId: string;
  projectId: string;
  workflowId: string;
  onSubmit?: (submissionDto: UserSubmissionDto) => void;
  onTeamSubmit?: (estimation: TeamProjectEstimation) => void;
  onSubmitProjectEstimation?: (dto: ProjectEstimationSubmissionDto) => Promise<void> | void;
  canSubmit?: boolean;
  currentStage?: CurrentProjectStage | null;
  className?: string;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

// ── Shared read-only quote display ──────────────────────────────────────────
const EstimateCard: React.FC<{
  estimation: TeamProjectEstimation;
  action?: React.ReactNode;
}> = ({ estimation, action }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-500">Billing type</span>
      <span
        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
          estimation.billingType === 'Hourly'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-purple-100 text-purple-700'
        }`}
      >
        {estimation.billingType}
      </span>
    </div>

    {estimation.billingType === 'Hourly' && estimation.estimatedHours != null && (
      <>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500">Estimated hours</span>
          <span className="text-sm text-gray-900">{estimation.estimatedHours}h</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500">Rate</span>
          <span className="text-sm text-gray-900">
            {formatCurrency(estimation.hourlyRateUsd ?? 0)} / hr
          </span>
        </div>
      </>
    )}

    <div className="flex items-center justify-between border-t border-gray-100 pt-3">
      <span className="text-base font-semibold text-gray-800">Total estimate</span>
      <span className="text-xl font-bold text-gray-900">{formatCurrency(estimation.totalUsd)}</span>
    </div>

    {estimation.notes && (
      <div className="rounded-lg bg-gray-50 p-3">
        <p className="text-xs font-medium text-gray-500 mb-1">Notes from the team</p>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{estimation.notes}</p>
      </div>
    )}

    {action && <div className="pt-1">{action}</div>}
  </div>
);

// ── Main component ───────────────────────────────────────────────────────────
const ProjectEstimationEnrollmentView: React.FC<Props> = ({
  enrollment,
  teamEstimation,
  isOwner,
  isTeamMember,
  teamId,
  nodeId,
  projectId,
  workflowId,
  onSubmit,
  onTeamSubmit,
  onSubmitProjectEstimation,
  canSubmit: canSubmitProp,
  currentStage,
  className = '',
}) => {
  const formId = useId();
  const [billingType, setBillingType] = useState<BillingType>(enrollment.defaultBillingType);
  const [estimatedHours, setEstimatedHours] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [totalUsd, setTotalUsd] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const computedTotal =
    billingType === 'Hourly' && estimatedHours && hourlyRate
      ? parseFloat(estimatedHours) * parseFloat(hourlyRate)
      : null;

  const canSubmit =
    billingType === 'Hourly'
      ? !!estimatedHours && !!hourlyRate && parseFloat(estimatedHours) > 0 && parseFloat(hourlyRate) > 0
      : !!totalUsd && parseFloat(totalUsd) > 0;

  const handleTeamFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const total =
      billingType === 'Hourly'
        ? parseFloat(estimatedHours) * parseFloat(hourlyRate)
        : parseFloat(totalUsd);

    if (isNaN(total) || total <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmitProjectEstimation?.({
        workflowId,
        nodeId,
        projectId,
        billingType,
        estimatedHours: billingType === 'Hourly' ? parseFloat(estimatedHours) : null,
        hourlyRateUsd: billingType === 'Hourly' ? parseFloat(hourlyRate) : null,
        totalUsd: total,
        notes,
      });
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptEstimate = () => {
    setIsAccepting(true);
    onSubmit?.({
      workflowId,
      nodeId,
      projectId,
      userSubmission: { type: 'ProjectEstimationSubmission' } as any,
    });
  };

  // ── Customer: team hasn't submitted yet ─────────────────────────────────────
  if (isOwner && !isTeamMember && !teamEstimation) {
    return (
      <div className={`p-6 text-center ${className}`} role="status" aria-live="polite">
        <div
          className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 mb-3"
          aria-hidden="true"
        >
          <svg
            className="h-6 w-6 text-amber-500"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-gray-900">Estimate in progress</h3>
        <p className="mt-1 text-sm text-gray-500 max-w-xs mx-auto">
          The team is reviewing your project and preparing a custom estimate. It'll appear here once
          it's ready.
        </p>
      </div>
    );
  }

  // ── Customer: quote ready to review and accept ───────────────────────────────
  if (isOwner && !isTeamMember && teamEstimation) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div>
          <h3 className="text-base font-semibold text-gray-900">Your estimate is ready</h3>
          <p className="text-sm text-gray-500 mt-0.5">Review the details below and accept to continue.</p>
        </div>
        <EstimateCard
          estimation={teamEstimation}
          action={
            <div className="space-y-2">
              {error && (
                <p className="text-sm text-red-600 rounded-lg bg-red-50 px-3 py-2" role="alert">
                  {error}
                </p>
              )}
              <button
                onClick={handleAcceptEstimate}
                disabled={isAccepting}
                className="w-full py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-busy={isAccepting}
              >
                {isAccepting ? 'Accepting…' : 'Accept Estimate'}
              </button>
              <p className="text-xs text-center text-gray-400">
                By accepting you agree to proceed with this quoted amount.
              </p>
            </div>
          }
        />
      </div>
    );
  }

  // ── Team member: already submitted ──────────────────────────────────────────
  if (isTeamMember && teamEstimation) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                clipRule="evenodd"
              />
            </svg>
            Estimate submitted
          </span>
        </div>
        <EstimateCard estimation={teamEstimation} />
      </div>
    );
  }

  // ── Team member: submission form ─────────────────────────────────────────────
  return (
    <form
      id={formId}
      onSubmit={handleTeamFormSubmit}
      className={`space-y-5 ${className}`}
      aria-label="Submit project estimate"
      noValidate
    >
      <div>
        <h3 className="text-base font-semibold text-gray-900">Submit project estimate</h3>
        <p className="text-sm text-gray-500 mt-0.5">The customer will review this before continuing.</p>
      </div>

      {/* Billing type toggle */}
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-2">Billing type</legend>
        <div className="flex gap-3">
          {(['Fixed', 'Hourly'] as BillingType[]).map((type) => (
            <label
              key={type}
              className={`flex-1 flex items-center justify-center gap-2 rounded-lg border-2 py-2.5 text-sm font-medium cursor-pointer transition-colors select-none ${
                billingType === type
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={`${formId}-billingType`}
                value={type}
                checked={billingType === type}
                onChange={() => {
                  setBillingType(type);
                  setEstimatedHours('');
                  setHourlyRate('');
                  setTotalUsd('');
                }}
                className="sr-only"
              />
              {type}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Hourly-specific fields */}
      {billingType === 'Hourly' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor={`${formId}-hours`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Estimated hours
              </label>
              <input
                id={`${formId}-hours`}
                type="number"
                min="0.5"
                step="0.5"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(e.target.value)}
                required
                placeholder="e.g. 10"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor={`${formId}-rate`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Rate (USD / hr)
              </label>
              <input
                id={`${formId}-rate`}
                type="number"
                min="0"
                step="0.01"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                required
                placeholder="e.g. 150"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          {computedTotal != null && (
            <div className="flex items-center justify-between rounded-lg bg-blue-50 px-4 py-3">
              <span className="text-sm font-medium text-blue-700">Computed total</span>
              <span className="text-base font-bold text-blue-900">{formatCurrency(computedTotal)}</span>
            </div>
          )}
        </div>
      )}

      {/* Fixed total */}
      {billingType === 'Fixed' && (
        <div>
          <label
            htmlFor={`${formId}-total`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Total amount (USD)
          </label>
          <input
            id={`${formId}-total`}
            type="number"
            min="0"
            step="0.01"
            value={totalUsd}
            onChange={(e) => setTotalUsd(e.target.value)}
            required
            placeholder="e.g. 1500"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      )}

      {/* Notes */}
      <div>
        <label htmlFor={`${formId}-notes`} className="block text-sm font-medium text-gray-700 mb-1">
          Notes <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          id={`${formId}-notes`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Scope, timeline, terms, or anything the customer should know…"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 rounded-lg bg-red-50 px-3 py-2" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting || !canSubmit}
        className="w-full py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-busy={isSubmitting}
      >
        {isSubmitting ? 'Submitting…' : 'Submit Estimate'}
      </button>
    </form>
  );
};

export default ProjectEstimationEnrollmentView;
